import type { User } from '@lumi.new/sdk'
import { lumi } from '@/lib/lumi'
import { useEffect, useState } from 'react'

export type AccountRole = 'candidate' | 'recruiter' | 'organisme'

export interface AccountProfile {
  email: string
  role: AccountRole
  fullName?: string
  company?: string
  phone?: string
  city?: string
  diploma?: string
  createdAt: string
}

const PROFILES_KEY = 'bnssa_mns_profiles'
const CURRENT_PROFILE_KEY = 'bnssa_mns_current_profile'

/**
 * Adresse e-mail du compte administrateur autorisé à choisir librement son
 * espace dashboard (candidat / recruteur / organisme). Seul ce compte voit
 * le sélecteur d'espace dans le header.
 */
export const ADMIN_EMAIL = 'h2oxav@gmail.com'

/** Vrai si l'email correspond au compte administrateur. */
export function isAdminEmail(email: string | undefined | null): boolean {
  return !!email && email.trim().toLowerCase() === ADMIN_EMAIL
}

/**
 * Read all stored business profiles from localStorage.
 * Profiles are keyed by email and persist role/contact info that the Lumi SDK
 * does not natively store on User.
 */
function readProfiles(): Record<string, AccountProfile> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(PROFILES_KEY)
    return raw ? (JSON.parse(raw) as Record<string, AccountProfile>) : {}
  } catch {
    return {}
  }
}

function writeProfiles(profiles: Record<string, AccountProfile>) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles))
  } catch {
    /* ignore quota errors */
  }
}

/**
 * Persist a business profile (role + extra metadata) for a given email.
 * Called on signup once the Lumi SDK has confirmed the account.
 */
export function saveProfile(profile: AccountProfile) {
  const all = readProfiles()
  all[profile.email.toLowerCase()] = profile
  writeProfiles(all)
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(CURRENT_PROFILE_KEY, profile.email.toLowerCase())
    } catch {
      /* ignore */
    }
  }
}

export function getProfileByEmail(email: string | undefined | null): AccountProfile | null {
  if (!email) return null
  const all = readProfiles()
  return all[email.toLowerCase()] ?? null
}

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(lumi.auth.isAuthenticated)
  const [user, setUser] = useState<User | null>(lumi.auth.user)
  const [profile, setProfile] = useState<AccountProfile | null>(
    getProfileByEmail(lumi.auth.user?.email)
  )

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ isAuthenticated, user }) => {
      setIsAuthenticated(isAuthenticated)
      setUser(user)
      setProfile(getProfileByEmail(user?.email))
    })
    return () => unsubscribe()
  }, [])

  const signOut = async () => {
    await lumi.auth.signOut()
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(CURRENT_PROFILE_KEY)
      } catch {
        /* ignore */
      }
    }
  }

  return {
    user,
    profile,
    isAuthenticated,
    isAdmin: user?.userRole === 'ADMIN' || isAdminEmail(user?.email),
    isCandidate: profile?.role === 'candidate',
    isRecruiter: profile?.role === 'recruiter',
    isOrganisme: profile?.role === 'organisme',
    signOut,
  }
}
